<template>
  <div>
    <div class="header-section">
      <h1>Servers</h1>
      <button @click="reload" class="refresh-button" :disabled="loading">
        <span class="refresh-icon">🔄</span> Uppdatera
      </button>
    </div>

    <div v-if="loading" class="servers-loading">
      <div class="skeleton-grid">
        <ServerComponent v-for="n in 6" :key="'skel-' + n" :loading="true" :icon="placeholderImage" :iconIsEmoji="false" :text="''" :link="'#'" :description="''" />
      </div>
    </div>
    <div v-else-if="fetchError" class="error-section">
      <div class="error-message">
        <span class="error-icon">⚠️</span>
        Fel: {{ fetchError }}
      </div>
      <button @click="reload" class="retry-button">Försök igen</button>
    </div>
    <div v-else-if="servers.length === 0" class="empty-section">
      <span class="empty-icon">📭</span>
      Inga servrar hittades.
    </div>

    <div v-else class="server-groups">
      <div v-for="group in displayGroups" :key="group.name" class="server-group">
        <h2 class="group-title">{{ group.name }}</h2>
        <div class="buttons-list">
          <ServerComponent
            v-for="s in group.items"
            :key="s.name"
            :icon="s.icon"
            :iconIsEmoji="true"
            :text="s.name"
            :link="s.url"
            :description="s.description"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ServerComponent from '@/components/ServerComponent.vue'
import { fetchServerList, refreshServerList } from '@/services/serverApi'
import serverBlacklist from '@/data/serverBlacklist.json'

export default {
  name: 'ServerView',
  components: { ServerComponent },
  data() {
    return {
      loading: false,
      error: null,
      fetchError: null,
      servers: [],
      placeholderImage: 'https://via.placeholder.com/40'
    }
  },
  computed: {
    // blacklist från JSON-fil
    blacklist() {
      return serverBlacklist.blacklist || []
    },
    displayGroups() {
      return this.servers.map(group => {
        const filteredItems = group.items.filter(s => {
          // Extrahera domännamnet från URL eller namn
          let domain = ''
          if (s.url) {
            try {
              const url = new URL(s.url)
              domain = url.hostname.toLowerCase()
            } catch {
              domain = s.name.toLowerCase()
            }
          } else {
            domain = s.name.toLowerCase()
          }
          
          // Kontrollera mot blacklist
          return !this.blacklist.includes(domain)
        })
        
        return {
          name: group.name,
          items: filteredItems
        }
      }).filter(group => group.items.length > 0)
    }
  },
  created() {
    this.loadServers()
  },
  methods: {
    async loadServers() {
      this.fetchError = null
      this.loading = true
      try {
        const serverList = await fetchServerList()
        this.servers = serverList
      } catch (err) {
        this.fetchError = err.message || String(err)
      } finally {
        this.loading = false
      }
    },
    async reload() {
      this.fetchError = null
      this.loading = true
      try {
        const serverList = await refreshServerList()
        this.servers = serverList
      } catch (err) {
        this.fetchError = err.message || String(err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 20px;
}

.refresh-button {
  background: var(--color-primary);
  color: var(--color-on-primary);
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-icon {
  transition: transform 0.3s ease;
}

.refresh-button:hover .refresh-icon {
  transform: rotate(180deg);
}

.server-groups {
  padding: 20px;
}

.server-group {
  margin-bottom: 30px;
}

.group-title {
  margin-left: 10px;
  margin-bottom: 15px;
  font-size: 1.4em;
  color: var(--color-text);
  border-bottom: 2px solid var(--color-border);
  padding-bottom: 8px;
  font-weight: 600;
}

.buttons-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: flex-start;
}

.error-section {
  text-align: center;
  padding: 40px 20px;
}

.error-message {
  color: #c0392b;
  margin-bottom: 16px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.error-icon {
  font-size: 20px;
}

.retry-button {
  background: #e74c3c;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.retry-button:hover {
  background: #c0392b;
}

.empty-section {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
}

.empty-icon {
  display: block;
  font-size: 48px;
  margin-bottom: 16px;
}

/* Skeletons */
.skeleton { 
  background: linear-gradient(90deg, #eee, #f5f5f5, #eee); 
  border-radius: 4px; 
  animation: skeleton-loading 1.5s infinite ease-in-out;
}

@keyframes skeleton-loading {
  0%, 100% {
    background: linear-gradient(90deg, #eee, #f5f5f5, #eee);
  }
  50% {
    background: linear-gradient(90deg, #f5f5f5, #eee, #f5f5f5);
  }
}

.skeleton-grid { 
  display: flex; 
  gap: 20px; 
  flex-wrap: wrap; 
  padding: 20px;
}

.servers-loading {
  padding: 20px;
}
</style>
